import type { Risk as RiskDto, TrackedAmount, TrackedAmountBody, UpdateRiskBody } from "Infrastructure/ApiZfp/Schema";

export type RiskFormValues = {
  currentAmount: number;
  currentAgeTo: number;
  recommendedAmount: number;
  recommendedAgeTo: number;
  requiredBaseAmount: number;
  infinityRent: number;
  requiredCoverageCompensation: number;
};

/**
 * The `risk` prop can be stale while a previous update is still in flight, so its `hasChanged`
 * flag must not be copied blindly: a pending edit that has not been persisted/refetched yet
 * would otherwise be sent back as `hasChanged: false` by the next field's update. Derive the
 * flag from the live form state instead and only fall back to the server value when the form
 * matches it.
 */
const toTrackedAmount = (current: TrackedAmount, formValue: number): TrackedAmountBody => ({
  value: { ...current.value, value: formValue },
  hasChanged: current.hasChanged || formValue !== current.value.value,
});

export const buildUpdateRiskBody = (risk: RiskDto, formValues: RiskFormValues, updates: Partial<UpdateRiskBody>): UpdateRiskBody => ({
  currentAmount: { ...risk.currentAmount, value: formValues.currentAmount },
  currentAgeTo: formValues.currentAgeTo,
  currentInterestRate: risk.currentInterestRate.value,
  recommendedAmount: { ...risk.recommendedAmount, value: formValues.recommendedAmount },
  recommendedAgeTo: formValues.recommendedAgeTo,
  requiredBaseAmount: toTrackedAmount(risk.requiredBaseAmount, formValues.requiredBaseAmount),
  infinityRent: toTrackedAmount(risk.infinityRent, formValues.infinityRent),
  requiredCoverageCompensation: toTrackedAmount(risk.requiredCoverageCompensation, formValues.requiredCoverageCompensation),
  // An explicit override in `updates` always wins over the derived value above.
  ...updates,
});

export type SerialQueue = <T>(task: () => Promise<T>) => Promise<T>;

/**
 * Risk updates send the complete body, so two of them in flight at once can be applied by the
 * server in either order and an older snapshot landing last would revert the newer values. The
 * queue sends the next request only after the previous one finished; a failure does not block it.
 */
export const createSerialQueue = (): SerialQueue => {
  let tail: Promise<unknown> = Promise.resolve();

  return <T>(task: () => Promise<T>) => {
    const result = tail.then(task, task);
    tail = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  };
};
